import React, { useState } from 'react';
import styled from '@emotion/styled';
import { colors } from '../theme/colors';
import { storeFile } from '../utils/fileStorage';

const UploadContainer = styled.div`
  border: ${props => props.hasAvailableDocs ? 'none' : `2px dashed ${colors.dropzoneBorder}`};
  border-radius: 8px;
  padding: ${props => props.hasAvailableDocs ? '0.75rem' : '1.5rem'};
  text-align: center;
  transition: all 0.2s;
  cursor: ${props => props.hasAvailableDocs ? 'default' : 'pointer'};
  background-color: ${props => props.hasAvailableDocs ? 'transparent' : colors.dropzoneBackground};
  min-height: ${props => props.hasFile ? '120px' : 'auto'};
  
  &:hover {
    border-color: ${props => props.hasAvailableDocs ? 'transparent' : colors.dropzoneBorderActive};
    background-color: ${props => props.hasAvailableDocs ? 'transparent' : colors.dropzoneHoverBackground};
  }
  
  ${props => props.hasFile && `
    border-color: ${colors.dropzoneBorder};
    background-color: ${colors.dropzoneHasFileBackground};
    border-style: solid;
    
    &:hover {
      background-color: ${colors.dropzoneHasFileBackground};
      opacity: 0.9;
    }
  `}
  
  ${props => props.isDragOver && `
    border-color: ${colors.dropzoneBorderActive};
    background-color: ${colors.dropzoneHoverBackground};
    border-style: solid;
    transform: scale(1.02);
  `}
`;

const Label = styled.label`
  display: block;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: ${props => props.hasFile ? '#ffffff' : '#2c3e50'};
  text-shadow: ${props => props.hasFile ? '0 1px 2px rgba(0, 0, 0, 0.1)' : 'none'};
`;

const FileInput = styled.input`
  display: none;
`;

const FileList = styled.div`
  margin-top: 0.5rem;
  text-align: left;
`;

const FileItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  padding: 0.25rem 0.5rem;
  background-color: rgba(255, 255, 255, 0.9);
  border-radius: 4px;
  font-size: 0.875rem;
  cursor: grab;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: rgba(255, 255, 255, 1);
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
  
  &:active {
    cursor: grabbing;
  }
  
  &.dragging {
    opacity: 0.5;
    transform: rotate(5deg);
  }
`;

const FileIcon = styled.span`
  font-size: 1rem;
`;

const FileName = styled.span`
  flex: 1;
  color: #000000;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const PriorityBadge = styled.span`
  background-color: ${props => props.isPrimary ? '#10b981' : '#6b7280'};
  color: white;
  padding: 0.125rem 0.375rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
`;

const FileInfo = styled.div`
  margin-top: 0.5rem;
  font-size: 0.9rem;
  color: #7f8c8d;
`;

const Required = styled.span`
  color: #e74c3c;
  margin-left: 0.25rem;
`;

// Helper function to extract date from filename
const extractDateFromFilename = (filename) => {
  const datePatterns = [
    /(\d{4})-(\d{2})-(\d{2})/, // YYYY-MM-DD
    /(\d{2})-(\d{2})-(\d{4})/, // MM-DD-YYYY
    /(\d{2})\/(\d{2})\/(\d{4})/, // MM/DD/YYYY
    /(\d{4})(\d{2})(\d{2})/, // YYYYMMDD
    /(\d{2})(\d{2})(\d{4})/, // MMDDYYYY
  ];
  
  for (const pattern of datePatterns) {
    const match = filename.match(pattern);
    if (match) {
      let year, month, day;
      // Check pattern type by looking at the pattern string
      const patternStr = pattern.toString();
      if (patternStr.includes('(\\d{4})') && (patternStr.includes('-') || !patternStr.includes('(\\d{2})(\\d{2})'))) {
        year = parseInt(match[1]);
        month = parseInt(match[2]);
        day = parseInt(match[3]);
      } else {
        month = parseInt(match[1]);
        day = parseInt(match[2]);
        year = parseInt(match[3]);
      }
      
      const date = new Date(year, month - 1, day);
      return date.getTime();
    }
  }
  
  return Date.now();
};

function MultiFileUpload({ 
  label, 
  fileType, 
  files = [], // Array of files
  primaryFile, // The primary file (newest)
  onChange, 
  onMoveFile, // Function to move files between types
  onAddToChunkingModal, // NEW: Callback to add file to chunking modal (one file only)
  accept, 
  required = false,
  availableDocuments = [],
  onPreviewDocument 
}) {
  const [isDragOver, setIsDragOver] = useState(false);
  
  const getFileName = (file) => {
    if (!file) return null;
    if (typeof file === 'string') {
      return file.split('\\').pop().split('/').pop();
    }
    return file.name;
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      // If onAddToChunkingModal callback exists, use new flow (add to chunking modal)
      if (onAddToChunkingModal) {
        onAddToChunkingModal(fileType, selectedFile);
      } else {
        // Legacy flow: add directly to Redux
        onChange(fileType, selectedFile);
      }
    }
  };

  const handleClick = () => {
    document.getElementById(`file-input-${fileType}`).click();
  };

  const handleDragStart = (e, file, index) => {
    e.dataTransfer.setData('application/json', JSON.stringify({
      file,
      fromType: fileType,
      fromIndex: index
    }));
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragOver(false);
    
    // Handle external file drops (from Windows Explorer, etc.)
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const droppedFiles = Array.from(e.dataTransfer.files);
      console.log(`🔍 [MULTIFILE DEBUG] Dropped ${droppedFiles.length} files`);
      
      // NEW FLOW: Only accept one file, add to chunking modal
      if (onAddToChunkingModal) {
        if (droppedFiles.length > 1) {
          alert('Please drop only one file at a time. Use directory selection for multiple files.');
          return;
        }
        
        const file = droppedFiles[0];
        console.log(`🔍 [MULTIFILE DEBUG] Single file mode - adding to chunking modal: ${file.name}`);
        onAddToChunkingModal(fileType, file);
        return;
      }
      
      // LEGACY FLOW: Multiple files, add to Redux
      for (const file of droppedFiles) {
        console.log(`🔍 [MULTIFILE DEBUG] Processing file: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)}MB)`);
        
        if (onChange) {
          // Check if file needs compression (for TIFF files >5MB)
          const needsCompression = file.name.toLowerCase().match(/\.(tif|tiff)$/) && file.size > 5 * 1024 * 1024;
          
          if (needsCompression) {
            console.log(`🔍 [MULTIFILE DEBUG] TIFF file >5MB detected, starting compression...`);
            try {
              // Import the optimization function
              const { optimizeFileForUpload } = await import('../utils/fileOptimization');
              const optimizedFile = await optimizeFileForUpload(file);
              
              console.log(`🔍 [MULTIFILE DEBUG] Compression complete: ${file.size / 1024 / 1024}MB → ${optimizedFile.size / 1024 / 1024}MB`);
              
              // Convert optimized File object to metadata that can be stored in Redux
              const fileMetadata = storeFile(optimizedFile);
              onChange(fileType, fileMetadata);
            } catch (error) {
              console.error(`🔍 [MULTIFILE DEBUG] Compression failed:`, error);
              // Fallback to original file
              const fileMetadata = storeFile(file);
              onChange(fileType, fileMetadata);
            }
          } else {
            console.log(`🔍 [MULTIFILE DEBUG] File does not need compression`);
            // Convert File object to metadata that can be stored in Redux
            const fileMetadata = storeFile(file);
            onChange(fileType, fileMetadata);
          }
        }
      }
      return;
    }
    
    // Handle internal drag-and-drop between sections
    try {
      const dragData = JSON.parse(e.dataTransfer.getData('application/json'));
      if (dragData.fromType !== fileType && onMoveFile) {
        onMoveFile(dragData.file, dragData.fromType, fileType, dragData.fromIndex);
      }
    } catch (error) {
      // Only log error if it's not an empty dataTransfer (which is normal for external drops)
      if (e.dataTransfer.getData('application/json')) {
        console.error('Error handling internal file drop:', error);
      }
    }
  };

  const hasFiles = files.length > 0;

  // Sort files by date (newest first)
  const sortedFiles = [...files].sort((a, b) => {
    const dateA = extractDateFromFilename(getFileName(a) || '');
    const dateB = extractDateFromFilename(getFileName(b) || '');
    return dateB - dateA;
  });

  return (
    <UploadContainer 
      hasFile={hasFiles} 
      hasAvailableDocs={availableDocuments.length > 0} 
      isDragOver={isDragOver}
      onClick={handleClick}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <Label hasFile={hasFiles}>
        {label}
        {required && <Required>*</Required>}
      </Label>
      
      <FileInput
        id={`file-input-${fileType}`}
        type="file"
        accept={accept}
        onChange={handleFileChange}
      />
      
      {hasFiles ? (
        <FileList>
          {sortedFiles.map((file, index) => {
            const fileName = getFileName(file);
            const isPrimary = index === 0; // First file is primary (newest)
            
            return (
              <FileItem 
                key={index}
                draggable={true}
                onDragStart={(e) => handleDragStart(e, file, index)}
              >
                <FileIcon>📄</FileIcon>
                <FileName title={fileName}>{fileName}</FileName>
                <PriorityBadge isPrimary={isPrimary}>
                  {isPrimary ? 'Primary' : `${index + 1}`}
                </PriorityBadge>
              </FileItem>
            );
          })}
        </FileList>
      ) : (
        <FileInfo>
          Click to upload or drag and drop
          <br />
          <small>Accepted: {accept}</small>
          {onMoveFile && (
            <><br /><small style={{ color: '#6b7280', fontStyle: 'italic' }}>
              Drag files here from other sections
            </small></>
          )}
        </FileInfo>
      )}
    </UploadContainer>
  );
}

export default MultiFileUpload;
